import { TouchableOpacity, Text, ActivityIndicator, View, StyleSheet } from 'react-native'
import { mesaColorNames, typography, classes } from '@/src/common/theme'
import { type ComponentProps, useState } from 'react'
import { ComingSoon } from '@/src/common/modules/RewardsSection/ComingSoon'
import { useMediaQuery } from 'react-responsive'
import { useLogError } from '@/src/common/hooks/useLogError'
import { DesignSystem } from '@/src/design-system'
import type { RewardsAction } from '@/src/experiences/rewards/hooks/useRewardsActions'
import { useSegmentDomain } from '@/src/services/segment/SegmentDomainProvider'
import { useOpenRedemptionPortal } from '@/src/common/hooks/useOpenRedemptionPortal'
import { Link } from 'expo-router'
import { Skeleton } from '@/src/design-system/Skeleton'
import { cardBorderRadius } from '@/src/design-system/card/Card'

export function RewardsComponentsActionButton({
  style: styleProp,
  rewardsAction: props,
}: {
  rewardsAction: RewardsAction
} & Pick<ComponentProps<typeof TouchableOpacity>, 'style'>) {
  const [loading, setLoading] = useState(false)
  const minWidth500 = useMediaQuery({ minWidth: 500 })
  const logError = useLogError()
  const { triggerAction } = useSegmentDomain()
  const disabled = 'disabled' in props && props.disabled
  const { openRedemptionPortal } = useOpenRedemptionPortal()

  const style = StyleSheet.flatten([styles.rewardOptionEntryPoint, styleProp])

  if ('initializing' in props && props.initializing) {
    return (
      <Skeleton
        style={[classes.cardShadow, { borderRadius: cardBorderRadius, overflow: 'hidden' }, style]}
      />
    )
  }

  const button = (
    <TouchableOpacity
      testID={props.testID}
      style={StyleSheet.flatten([style, !disabled && { cursor: 'pointer' }])}
      disabled={!('onPress' in props) && !!disabled}
      onPress={(e) => {
        void triggerAction(props.testID, 'Pressed')

        if ('onPress' in props && props.onPress) props.onPress(e)
        else if ('redemptionPortal' in props && !disabled)
          void (async () => {
            try {
              setLoading(true)
              await openRedemptionPortal(props.redemptionPortal.title, {
                variables: { redirectPath: props.redemptionPortal.path },
              })
            } catch (error) {
              logError('error opening reward action', error)
            } finally {
              setLoading(false)
            }
          })()
      }}
    >
      <DesignSystem.Card
        style={[styles.rewardOptionCard, { paddingHorizontal: minWidth500 ? 16 : 8 }]}
      >
        {loading && <ActivityIndicator size="large" style={styles.rewardOptionLoading} />}
        {disabled === 'isComingSoon' && <ComingSoon style={styles.comingSoon} />}
        <View style={{ flex: 1, width: '100%' }}>
          {'disabled' in props ? (
            <props.Icon width="100%" height="100%" grayscale={!!disabled} />
          ) : (
            <props.Icon width="100%" height="100%" />
          )}
        </View>
        <Text style={styles.rewardOptionTitle}>{props.label}</Text>
      </DesignSystem.Card>
    </TouchableOpacity>
  )

  return 'route' in props && !disabled ? (
    <Link href={props.route} asChild>
      {button}
    </Link>
  ) : (
    button
  )
}

const styles = StyleSheet.create({
  rewardOptionEntryPoint: {
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'stretch',
    aspectRatio: 1,
  },
  rewardOptionLoading: {
    position: 'absolute',
    borderRadius: 15,
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    zIndex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  rewardOptionTitle: {
    ...typography['label/xs-regular'],
    minHeight: 28,
    marginTop: 8,
    color: mesaColorNames['Mesa - Brand/Blackish #222222'],
    textAlign: 'center',
  },
  rewardOptionCard: {
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
  },
  comingSoon: {
    position: 'absolute',
    top: -5,
    zIndex: 1,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
})
